import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Cache from "../utils/cache";

export default function WebsiteDeals() {
  const [offers, setOffers] = useState([]);
  const [vendorsData, setVendorsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("vendorAsc"); // vendorAsc, vendorDesc, startDateAsc, startDateDesc

  useEffect(() => {
    const cached = Cache.getData();

    if (cached.websiteOffers && cached.websiteOffers.length > 0) {
      setOffers(cached.websiteOffers);
      setLoading(false);

      if (cached.vendorsData) {
        setVendorsData(cached.vendorsData);
      } else {
        fetchVendorData(cached.websiteOffers);
      }

      return;
    }

    async function fetchWebsiteOffersAndVendors() {
      setLoading(true);
      try {

        // Fetch batched documents
        const snap = await getDocs(collection(db, "website_offers"));
        // Flatten offers arrays from all batch documents
        let allOffers = [];
        snap.docs.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.offers)) {
            allOffers.push(...data.offers);
          }
        });

        // Filter out expired offers
        function getUTCStartOfDay(date) {
          return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        }

        allOffers = allOffers.filter((offer) => {
          if (!offer.endDate) return true;

          const offerDate = offer.endDate?.toDate ? offer.endDate.toDate() : new Date(offer.endDate);

          const offerUTCDate = getUTCStartOfDay(offerDate);
          const nowUTCDate = getUTCStartOfDay(new Date());

          return offerUTCDate >= nowUTCDate;
        });

        // Sort by vendor name ascending by default
        allOffers.sort((a, b) => (a.vendor || "").localeCompare(b.vendor || ""));

        setOffers(allOffers);

        await fetchVendorData(allOffers);

        Cache.setData({
          ...cached,
          websiteOffers: allOffers,
        });
      } catch (err) {
        setError("Failed to load website deals: " + err.message);
      } finally {
        setLoading(false);
      }
    }

    async function fetchVendorData(allOffers) {
      const vendorNames = [...new Set(allOffers.map((offer) => offer.vendor))];
      const vendorsObj = {};

      // Load all vendor arrays documents once to avoid multiple firestore calls
      let vendorDocs = [];
      try {
        const vendorsCollectionSnapshot = await getDocs(collection(db, "vendors"));
        vendorDocs = vendorsCollectionSnapshot.docs.filter((doc) =>
          doc.id.startsWith("vendors_")
        );
      } catch (e) {
        console.error("Error loading vendor groups:", e);
      }

      vendorNames.forEach((vendorName) => {
        if (!vendorName) return;

        let foundVendor = null;

        for (const vdoc of vendorDocs) {
          const data = vdoc.data();
          if (Array.isArray(data.vendors)) {
            foundVendor = data.vendors.find(
              (v) =>
                (v.vendorName && v.vendorName.toLowerCase() === vendorName.toLowerCase()) ||
                (v.id && v.id.toLowerCase() === vendorName.toLowerCase())
            );
            if (foundVendor) break;
          }
        }

        if (foundVendor) {
          vendorsObj[vendorName] = {
            logo: foundVendor.logo || null,
            address: foundVendor.address || null,
            categories: foundVendor.categories || [],
          };
        } else {
          vendorsObj[vendorName] = { logo: null, address: null, categories: [] };
          console.warn(`Vendor not found in vendors_* collections: ${vendorName}`);
        }
      });

      setVendorsData(vendorsObj);

      const cached = Cache.getData();
      Cache.setData({
        ...cached,
        vendorsData: vendorsObj,
      });
    }

    fetchWebsiteOffersAndVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortKey]);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Filtering and sorting remain unchanged (use offers from flattened array)

  const filteredOffers = useMemo(() => {
    const search = searchTerm.toLowerCase();

    if (!search) return offers;

    return offers.filter((offer) => {
      const vendorInfo = vendorsData[offer.vendor] || {};
      const categories = vendorInfo.categories || [];

      const matchVendor = (offer.vendor || "").toLowerCase().includes(search);
      const matchDescription = (offer.description || "").toLowerCase().includes(search);
      const matchCategory = categories.some((cat) =>
        cat.toLowerCase().includes(search)
      );

      return matchVendor || matchDescription || matchCategory;
    });
  }, [offers, vendorsData, searchTerm]);

  const sortedOffers = useMemo(() => {
    const sorted = [...filteredOffers];
    switch (sortKey) {
      case "vendorAsc":
        sorted.sort((a, b) => (a.vendor || "").localeCompare(b.vendor || ""));
        break;
      case "vendorDesc":
        sorted.sort((a, b) => (b.vendor || "").localeCompare(a.vendor || ""));
        break;
      case "startDateAsc":
        sorted.sort((a, b) => {
          const aDate = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate || 0);
          const bDate = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate || 0);
          return aDate - bDate;
        });
        break;
      case "startDateDesc":
        sorted.sort((a, b) => {
          const aDate = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate || 0);
          const bDate = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate || 0);
          return bDate - aDate;
        });
        break;
      default:
        break;
    }
    return sorted;
  }, [filteredOffers, sortKey]);


  const totalPages = Math.ceil(sortedOffers.length / itemsPerPage); // 🔹 New
  const paginatedOffers = sortedOffers.slice(                       // 🔹 New
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const now = new Date();

  const formatDate = (d) => {
    if (!d) return "N/A";
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6"> 
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">Website Offers</h1>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <input
          type="text"
          placeholder="Search by vendor, description or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded px-3 py-2 w-full sm:w-1/3"
        />

        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="border rounded px-3 py-2 w-full sm:w-1/4"
        >
          <option value="vendorAsc">Vendor: A to Z</option>
          <option value="vendorDesc">Vendor: Z to A</option>
          <option value="startDateAsc">Start Date: Oldest First</option>
          <option value="startDateDesc">Start Date: Newest First</option>
        </select>
      </div>

      {/* Offers Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader"></div>
          <style>{`
            .loader {
              border: 6px solid #f3f3f3;
              border-top: 6px solid #3b82f6;
              border-radius: 50%;
              width: 48px;
              height: 48px;
              animation: spin 1s linear infinite;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : sortedOffers.length === 0 ? (
        <p className="text-center text-gray-600">No matching website offers found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {paginatedOffers.map(
            (
              {
                id,
                vendor,
                description,
                promoCode,
                startDate,
                endDate,
                offerLink,
              },
              index
            ) => {
              const start = startDate?.toDate
                ? startDate.toDate()
                : startDate
                  ? new Date(startDate)
                  : null;
              const end = endDate?.toDate
                ? endDate.toDate()
                : endDate
                  ? new Date(endDate)
                  : null;
              const isComingSoon = start && start > now;
              const vendorInfo = vendorsData[vendor] || {};
              const logo = vendorInfo.logo;
              const address = vendorInfo.address;

              // Use id or fallback key combining vendor and index
              const uniqueKey = id || `${vendor}-${index}`;

              return (
                <div
                  key={uniqueKey}
                  onClick={() => {
                    if (address) window.open(address, "_blank", "noopener");
                  }}
                  className="border rounded-lg shadow p-4 flex flex-col items-center bg-white relative cursor-pointer hover:shadow-lg transition"
                  title={address ? `Go to ${vendor} website` : ""}
                >
                  {logo && (
                    <div className="w-24 h-24 flex items-center justify-center mb-4">
                      <img
                        src={logo}
                        alt={`${vendor} logo`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  )}

                  <h2 className="text-lg font-semibold mb-1 text-center max-w-full">
                    {vendor}
                  </h2>

                  {description && (
                    <p className="text-gray-700 text-sm mb-2 text-center whitespace-pre-wrap max-w-full">
                      {description}
                    </p>
                  )}

                  <p className="text-gray-700 text-sm font-semibold mb-2 text-center">
                    Start: {formatDate(start)} <br />
                    End: {formatDate(end)}
                  </p>

                  {promoCode && (
                    <p className="text-indigo-600 font-medium mb-2 text-center max-w-full">
                      Promo Code: {promoCode}
                    </p>
                  )}

                  {isComingSoon && (
                    <div
                      className="absolute top-3 right-3 px-2 py-1 rounded text-xs font-bold text-white"
                      style={{
                        backgroundColor: "#ef4444",
                        animation: "flash 1.5s infinite",
                      }}
                    >
                      Coming Soon
                    </div>
                  )}

                  <style>{`
                    @keyframes flash {
                      0%, 100% { opacity: 1; }
                      50% { opacity: 0.3; }
                    }
                  `}</style>
                </div>
              );
            }
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && !error && sortedOffers.length > 0 && (
        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"
          >
            Previous
          </button>

          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border rounded bg-white hover:bg-gray-100 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

    </div>
  );
}
